import {
  getMarketingHomeExpensesTotalKeyDates,
  MARKETING_HOME_EXPENSES_TOTAL,
} from '@/content/marketing/home-mock-data';
import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { isMarketingMockEnabled } from '@/lib/marketing-mock/is-marketing-mock-enabled';
import { getMarketingMockQueryOptions } from '@/lib/marketing-mock/query-options';
import api from '@/services/api';
import { Expense } from '@/types/expense';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const expenseKeys = {
  all: ['expenses'] as const,
  list: (params: {
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) => [...expenseKeys.all, 'list', params] as const,
  detail: (id: string) => [...expenseKeys.all, 'detail', id] as const,
  total: (startDate: string, endDate: string) =>
    [...expenseKeys.all, 'total', startDate, endDate] as const,
};

export interface UseExpensesParams {
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export function useExpenses(params: UseExpensesParams = {}) {
  const isHydrated = useAuthHydration();
  const { startDate, endDate, page = 1, limit = 50 } = params;

  return useQuery({
    queryKey: expenseKeys.list({ startDate, endDate, page, limit }),
    queryFn: async () => {
      const response = await api.get<{
        data: Expense[];
        total: number;
        page: number;
        limit: number;
      }>('/provider/expenses', {
        params: { startDate, endDate, page, limit },
      });
      return response.data;
    },
    enabled: isHydrated,
  });
}

export function useExpensesTotal(startDate: string, endDate: string) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: expenseKeys.total(startDate, endDate),
    queryFn: async () => {
      const response = await api.get<{ total: number }>(
        '/dashboard/expenses-total',
        { params: { startDate, endDate } },
      );
      return response.data.total;
    },
    enabled: isHydrated && !!startDate && !!endDate,
    ...getMarketingMockQueryOptions(
      isMarketingMockEnabled() &&
        startDate === getMarketingHomeExpensesTotalKeyDates().startDate &&
        endDate === getMarketingHomeExpensesTotalKeyDates().endDate
        ? MARKETING_HOME_EXPENSES_TOTAL
        : undefined,
    ),
  });
}

export interface CreateExpensePayload {
  amount: number;
  date: string;
  category?: string;
  description?: string;
}

export function useCreateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateExpensePayload) => {
      const response = await api.post<Expense>('/provider/expenses', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.all });
    },
  });
}

export interface UpdateExpensePayload {
  amount?: number;
  date?: string;
  category?: string;
  description?: string;
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateExpensePayload;
    }) => {
      const response = await api.patch<Expense>(
        `/provider/expenses/${id}`,
        payload,
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.id) {
        queryClient.invalidateQueries({
          queryKey: expenseKeys.detail(data.id),
        });
      }
      queryClient.invalidateQueries({ queryKey: expenseKeys.all });
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/provider/expenses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.all });
    },
  });
}
